import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Waves,
  Layers,
  Snowflake,
  Umbrella,
  Sailboat,
  ShoppingBag,
  Home,
  Heart,
  ArrowRight,
  ArrowUpRight,
  MapPin,
  Search,
  X,
  MessageCircle,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";

const SITE = "https://lagooncityrealestate.ca";

const GROUPS = [
  {
    id: "waterfront-and-canals",
    tag: "Waterfront & Canals",
    icon: Waves,
    faqs: [
      {
        q: "What does \u201cwaterfront\u201d mean in Lagoon City?",
        a: "In Lagoon City, waterfront generally means your property backs directly onto one of the community's interconnected canals or lagoons. Unlike traditional lakefront properties, most Lagoon City waterfront homes have canal frontage, with access to Lake Simcoe and the Trent-Severn Waterway through the canal system. The canals are one of the features that make Lagoon City so unique.",
      },
      {
        q: "Can I get from my backyard to Lake Simcoe by boat?",
        a: "Yes. One of Lagoon City's defining features is its interconnected canal system, which provides boating access to Lake Simcoe. However, not every waterfront property offers exactly the same boating experience. Canal location, water depth, turns, bridges and the particular route to the lake can all be important considerations when purchasing.",
      },
      {
        q: "Are all Lagoon City waterfront properties the same?",
        a: "No \u2014 and this is one of the most important things for buyers to understand. Two homes may both be advertised as \u201cwaterfront,\u201d but their canal locations, water depth, frontage, views, exposure, dock configuration and boating access can be very different.",
      },
      {
        q: "How deep are the canals?",
        a: "Canal depths can vary throughout Lagoon City and can change over time because of sediment and aquatic vegetation. The Township undertakes canal maintenance, including aquatic weed harvesting, skimming and dredging when required. If boating is important to you, I recommend evaluating the specific canal and property, rather than relying on a general statement about Lagoon City's water depth.",
      },
      {
        q: "Can I have a boat at my property?",
        a: "Most waterfront homeowners keep boats at their private docks, making it possible to step out of your home and onto the water.",
      },
      {
        q: "Can I kayak or paddleboard from my backyard?",
        a: "Absolutely. One of the great pleasures of Lagoon City is being able to enjoy the waterways directly from many waterfront properties. Kayaking, paddleboarding, canoeing and leisurely boating are all popular ways to enjoy the canal system.",
      },
    ],
  },
  {
    id: "shorewalls",
    tag: "Shorewalls",
    icon: Layers,
    faqs: [
      {
        q: "Who is responsible for the shorewall?",
        a: "The property owner is responsible for the construction and maintenance of the shorewall. This is one of the most important differences between Lagoon City waterfront ownership and some other waterfront communities. There are hundreds of shorewalls throughout Lagoon City, and their condition can vary considerably from property to property.",
      },
      {
        q: "Should I have a shorewall inspected before buying?",
        a: "If you're purchasing a waterfront property, I strongly recommend understanding the condition of the shorewall before firming up your purchase. A shorewall is a significant component of a canal-front property, and its condition can have financial implications for a future owner. Don't simply look at the house \u2014 look at the waterfront.",
      },
    ],
  },
  {
    id: "four-season-living",
    tag: "Four-Season Living",
    icon: Snowflake,
    faqs: [
      {
        q: "Is Lagoon City a year-round community?",
        a: "Absolutely. Although Lagoon City comes alive during the summer months, it is also home to many full-time residents who enjoy the community throughout the entire year. Winter brings a completely different atmosphere \u2014 quiet canals, snow-covered trails and a peaceful waterfront setting.",
      },
      {
        q: "Are the roads maintained during the winter?",
        a: "Lagoon City is a four-season community, and the roads are maintained throughout the winter. For buyers considering year-round living, winter accessibility is an important consideration \u2014 and one of the reasons Lagoon City works so well as a permanent home rather than simply a seasonal destination.",
      },
      {
        q: "What happens to the canals in winter?",
        a: "The canals are part of a year-round waterway system, but winter conditions naturally change how the waterways can be used. Water levels, ice conditions and individual dock configurations can all affect waterfront properties during the winter months. If winter waterfront access is important to you, the specific property should be evaluated rather than assuming every canal behaves exactly the same way.",
      },
    ],
  },
  {
    id: "beaches-parks-outdoor-living",
    tag: "Beaches, Parks & Outdoor Living",
    icon: Umbrella,
    faqs: [
      {
        q: "Does Lagoon City have its own beaches?",
        a: "Yes. Lagoon City has two private beaches for the use of Lagoon City residents and their guests. This is one of the features that sets Lagoon City apart from many other waterfront communities. You don't have to own a waterfront property to enjoy the water.",
      },
      {
        q: "Are there parks and walking paths?",
        a: "Yes. Lagoon City includes parks, footpaths and footbridges throughout the community. The Lagoon City Parks and Waterways Commission manages and maintains the private beaches, parks, canals, footbridges and footpaths.",
      },
      {
        q: "Is Lagoon City good for families?",
        a: "Yes. The combination of beaches, parks, waterways, recreational opportunities and a neighbourhood environment makes Lagoon City particularly appealing to families. Children can enjoy the outdoors, while adults appreciate having access to waterfront recreation without giving up the conveniences of a residential community.",
      },
      {
        q: "Is Lagoon City good for retirees?",
        a: "It can be an excellent choice for retirees who want a quieter waterfront lifestyle without giving up access to nearby services and communities. The ability to enjoy boating, walking, beaches and outdoor living right at home is a major attraction.",
      },
    ],
  },
  {
    id: "boating-and-water-access",
    tag: "Boating & Water Access",
    icon: Sailboat,
    faqs: [
      {
        q: "Can you boat all the way to Lake Simcoe?",
        a: "Yes. The interconnected canal system was designed to provide access to Lake Simcoe. However, boating conditions can vary by location, and buyers who own larger boats should carefully consider the specific route from the property to the lake.",
      },
      {
        q: "Is Lagoon City good for boating?",
        a: "If you love having the water as part of your everyday lifestyle, Lagoon City is hard to beat. You can enjoy boating, kayaking, paddleboarding and fishing without having to transport your boat to a public launch every time you want to get on the water.",
      },
      {
        q: "Are there marinas nearby?",
        a: "Yes. Lagoon City has a first-class marina and boating services, making the community particularly attractive to boaters. For buyers who are passionate about boating, I recommend looking at the entire boating lifestyle \u2014 not simply whether a property has a dock.",
      },
    ],
  },
  {
    id: "everyday-life",
    tag: "Everyday Life",
    icon: ShoppingBag,
    faqs: [
      {
        q: "Is Lagoon City isolated?",
        a: "No. Although Lagoon City feels peaceful and removed from the hustle and bustle, it is within easy driving distance of larger communities and everyday services. That's part of its appeal: you can enjoy a relaxed waterfront lifestyle without being completely removed from civilization.",
      },
      {
        q: "What is nearby?",
        a: "Residents have access to nearby restaurants, shopping, groceries, recreational facilities, marinas and other services, with larger shopping and medical services available in surrounding communities.",
      },
      {
        q: "How far is Orillia?",
        a: "Orillia is a 25 min drive from Lagoon City and provides access to a much broader range of shopping, restaurants, healthcare, entertainment and services.",
      },
      {
        q: "How far is Toronto?",
        a: "Just 90 minutes away from the GTA, Lagoon City is far enough away to feel like an escape from the city, while still being within reasonable driving distance for weekend visits and commuting.",
      },
    ],
  },
  {
    id: "buying-a-waterfront-home",
    tag: "Buying a Waterfront Home",
    icon: Home,
    faqs: [
      {
        q: "What should I look for when buying in Lagoon City?",
        a: "I encourage buyers to look beyond the interior of the house. Consider:",
        list: [
          "Canal location",
          "Water depth",
          "Access to Lake Simcoe",
          "Shorewall condition",
          "Dock condition and configuration",
          "Waterfront exposure and views",
          "Sun orientation",
          "Canal width",
          "Boat accessibility",
          "Parking",
          "Proximity to beaches and parks",
          "Future waterfront maintenance requirements",
        ],
        outro: "Not all Lagoon City waterfront properties offer the same lifestyle.",
      },
      {
        q: "Why are some Lagoon City waterfront homes more affordable than waterfront properties in Muskoka?",
        a: "This is one of the reasons Lagoon City attracts so many buyers. You can enjoy waterfront living, boating access, beaches and a four-season community without necessarily paying the price associated with some of Ontario's more expensive traditional lakefront markets. Lagoon City offers a different type of waterfront lifestyle \u2014 and for many buyers, that difference represents tremendous value.",
      },
      {
        q: "Is Lagoon City a good investment?",
        a: "A property should always be evaluated individually, but Lagoon City's combination of waterfront living, recreational amenities, proximity to Lake Simcoe and four-season community makes it an appealing market for both lifestyle buyers and investors. As with any real estate purchase, location, property condition, price and future market conditions all matter.",
      },
    ],
  },
  {
    id: "what-makes-lagoon-city-different",
    tag: "What Makes Lagoon City Different?",
    icon: Heart,
    faqs: [
      {
        q: "What makes Lagoon City special?",
        a: "It's the combination. It's not simply a waterfront home. It's taking the boat out from your backyard. It's walking to the beach. It's kayaking at sunset. It's having neighbours who become friends. It's enjoying summer on the water and peaceful winter mornings at home. And it's being part of a community where waterfront living isn't reserved for vacation \u2014 you can make it your everyday life.",
      },
      {
        q: "Why work with a Lagoon City specialist?",
        a: "Buying waterfront property requires a different level of understanding than buying an ordinary home. I've lived in Lagoon City for more than 20 years and it is my forever home. As a full-time resident and Realtor\u00ae with nearly 30 years of award-winning real estate experience, I understand that choosing the right Lagoon City property isn't simply about finding a beautiful house. It's about finding the right canal, the right waterfront, the right location and the right home for the way you want to live. My goal is to help buyers understand not only what they are buying \u2014 but why one Lagoon City property may be a better fit than another. Thinking about making Lagoon City home? Let's find the right waterfront lifestyle for you.",
      },
    ],
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function BuyersGuide() {
  const [query, setQuery] = useState("");
  const [showCta, setShowCta] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => setShowCta(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (f) =>
    !q ||
    f.q.toLowerCase().includes(q) ||
    f.a.toLowerCase().includes(q) ||
    (f.list || []).join(" ").toLowerCase().includes(q) ||
    (f.outro || "").toLowerCase().includes(q);
  const filteredGroups = GROUPS.map((g) => ({ ...g, faqs: g.faqs.filter(matches) })).filter(
    (g) => g.faqs.length
  );
  const resultCount = filteredGroups.reduce((n, g) => n + g.faqs.length, 0);

  const canonical = `${SITE}/buying-in-lagoon-city`;
  const title = "Lagoon City Buyer's Guide & FAQ | Buying Waterfront in Lagoon City, Ontario";
  const desc =
    "A local Realtor\u00ae's complete buyer's guide to Lagoon City, Ontario \u2014 canals, boating access to Lake Simcoe, shorewalls, private beaches, four-season living and what to look for when buying a waterfront home. By Joselle Alves.";

  const allFaqs = GROUPS.flatMap((g) => g.faqs);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.list ? `${f.a} ${f.list.join(", ")}. ${f.outro || ""}`.trim() : f.a,
      },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Lagoon City Buyer's Guide", item: canonical },
    ],
  };

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta
          name="keywords"
          content="Lagoon City buyer's guide, buying in Lagoon City, Lagoon City waterfront homes, Lagoon City canals, Lagoon City shorewall, Lake Simcoe waterfront, Lagoon City boating, Lagoon City beaches, buying waterfront Ontario, Lagoon City FAQ, Lagoon City real estate"
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${SITE}/assets/community/canals-aerial.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <PageHeader />

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src="/assets/community/canals-aerial.jpg"
            alt="Aerial view of the Lagoon City canals and waterfront homes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0E] via-[#0D0B0E]/75 to-[#0D0B0E]/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pt-24 pb-20 sm:pt-32 sm:pb-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1
              data-testid="buyers-guide-title"
              className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl"
            >
              Buying, Selling, or simply curious about <em className="italic text-[#E890A7]">Lagoon City?</em> Here, you&apos;ll find the answers you need.
            </h1>
            <div className="mt-6 max-w-2xl space-y-4 text-slate-300 font-light text-lg">
              <p>
                Lagoon City is unlike most waterfront communities in Ontario — with roughly 16 kilometres
                of interconnected canals, direct access to Lake Simcoe, private beaches, parks and a true
                four-season lifestyle. In many homes, you can step out your back door, hop in your boat or
                canoe, and be on the water within minutes.
              </p>
              <p>
                But buying in Lagoon City is about more than simply finding a home on the water. As a
                long-time resident and Realtor®, I know the community from both sides of the waterfront —
                as someone who lives here and as someone who helps others make it home.
              </p>
              <p>Here are some of the questions I hear most often.</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                data-testid="buyers-guide-hero-listings"
                to="/#properties"
                className="rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 flex items-center gap-2 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-all duration-300"
              >
                See homes for sale <ArrowRight size={16} />
              </Link>
              <Link
                data-testid="buyers-guide-hero-contact"
                to="/#contact"
                className="rounded-full border border-[#E890A7]/40 text-[#F2A7BA] px-7 py-3.5 hover:bg-[#E890A7]/10 transition-all duration-300"
              >
                Ask Joselle a question
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Table of contents */}
      <section className="max-w-5xl mx-auto px-6 -mt-10 relative z-10">
        <div
          className="rounded-[2rem] border border-[#E890A7]/20 bg-[#151218]/90 backdrop-blur-xl p-8 sm:p-10"
          data-testid="buyers-guide-toc"
        >
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7]/70 mb-5">On this page</p>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3">
            {GROUPS.map((g) => {
              const Icon = g.icon;
              return (
                <a
                  key={g.id}
                  href={`#${g.id}`}
                  data-testid={`buyers-guide-toc-${g.id}`}
                  className="flex items-center gap-3 text-slate-300 hover:text-[#F2A7BA] transition-colors group"
                >
                  <Icon size={15} className="text-[#E890A7] shrink-0" />
                  <span className="font-light border-b border-transparent group-hover:border-[#E890A7]/40">
                    {g.tag}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Search + FAQ sections */}
      <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
        <div className="sticky top-20 z-20 -mx-6 px-6 py-4 bg-[#0D0B0E]/92 backdrop-blur-xl mb-12">
          <div className="relative">
            <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#E890A7]/70" />
            <input
              data-testid="buyers-guide-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the guide — canals, shorewall, boating, beaches…"
              className="w-full rounded-full border border-[#E890A7]/25 bg-[#151218]/90 pl-13 pr-12 py-3.5 text-slate-100 placeholder:text-slate-500 font-light outline-none focus:border-[#E890A7]/60 transition-colors"
              style={{ paddingLeft: "3.25rem" }}
            />
            {query && (
              <button
                data-testid="buyers-guide-search-clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#F2A7BA] transition-colors"
              >
                <X size={18} />
              </button>
            )}
          </div>
          {q && (
            <p data-testid="buyers-guide-result-count" className="mt-3 pl-5 font-mono text-[11px] tracking-[0.2em] uppercase text-[#E890A7]/70">
              {resultCount} {resultCount === 1 ? "answer" : "answers"} found
            </p>
          )}
        </div>

        {filteredGroups.length === 0 ? (
          <div data-testid="buyers-guide-no-results" className="text-center py-16">
            <p className="font-display text-2xl text-slate-200">No answers matched "{query}"</p>
            <p className="mt-3 text-slate-400 font-light">
              Try a different word — or just{" "}
              <Link to="/#contact" className="text-[#F2A7BA] underline underline-offset-4 hover:text-[#E890A7]">
                ask Joselle directly
              </Link>
              .
            </p>
          </div>
        ) : (
          <div className="space-y-20">
            {filteredGroups.map((group) => {
              const Icon = group.icon;
              return (
                <motion.section
                  key={group.id}
                  id={group.id}
                  data-testid={`buyers-guide-section-${group.id}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7 }}
                  className="scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-8">
                    <span className="grid place-items-center h-11 w-11 rounded-2xl bg-[#E890A7]/12 border border-[#E890A7]/25">
                      <Icon size={20} className="text-[#E890A7]" />
                    </span>
                    <h2 className="font-display font-light text-3xl sm:text-4xl leading-tight">{group.tag}</h2>
                  </div>

                  <div className="space-y-8">
                    {group.faqs.map((f) => (
                      <article
                        key={f.q}
                        id={slug(f.q)}
                        data-testid={`buyers-guide-faq-${slug(f.q)}`}
                        className="scroll-mt-24 border-l-2 border-[#E890A7]/25 pl-6"
                      >
                        <h3 className="font-display text-xl sm:text-2xl text-slate-100 leading-snug">{f.q}</h3>
                        <p className="mt-3 text-slate-400 font-light leading-relaxed">{f.a}</p>
                        {f.list && (
                          <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2">
                            {f.list.map((item) => (
                              <li key={item} className="flex items-center gap-2.5 text-slate-300 font-light">
                                <span className="h-1.5 w-1.5 rounded-full bg-[#E890A7] shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        )}
                        {f.outro && <p className="mt-4 text-slate-400 font-light italic">{f.outro}</p>}
                      </article>
                    ))}
                  </div>
                </motion.section>
              );
            })}
          </div>
        )}
      </div>

      {/* Closing CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          className="rounded-[2rem] border border-[#E890A7]/25 bg-gradient-to-br from-[#1E1A23] to-[#151218] p-8 sm:p-12"
        >
          <div className="flex items-center gap-3 mb-5">
            <Home size={18} className="text-[#E890A7]" />
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7]">
              Ready to find your waterfront home?
            </p>
          </div>
          <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
            Let's find the right canal, the right waterfront and the right home for the way you want to live
          </h2>
          <p className="mt-5 max-w-2xl text-slate-400 font-light">
            As a Lagoon City specialist and full-time resident's advocate, with nearly 30 years in real estate
            and over 20 years of personal experience in Lagoon City, I know the area from both a professional and
            homeowner's perspective. Let me help you find your place here.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              data-testid="buyers-guide-cta-contact"
              to="/#contact"
              className="rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 flex items-center gap-2 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-all duration-300"
            >
              Talk to Joselle <ArrowRight size={16} />
            </Link>
            <Link
              data-testid="buyers-guide-cta-guide"
              to="/living-in-lagoon-city"
              className="rounded-full border border-[#E890A7]/40 text-[#F2A7BA] px-7 py-3.5 flex items-center gap-2 hover:bg-[#E890A7]/10 transition-all duration-300"
            >
              Read the neighbourhood guide <ArrowUpRight size={15} />
            </Link>
          </div>
          <p className="mt-8 flex items-center gap-2 text-sm text-slate-500">
            <MapPin size={14} className="text-[#E890A7]" /> Lagoon City, Brechin · Township of Ramara, Ontario
          </p>
        </motion.div>
      </section>

      {/* Floating "Talk to Joselle" CTA */}
      <Link
        to="/#contact"
        data-testid="buyers-guide-sticky-cta"
        className={`fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-5 py-3 shadow-xl shadow-black/40 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-[opacity,transform,background-color] duration-500 ${
          showCta ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <MessageCircle size={18} /> Talk to Joselle
      </Link>

      <Footer />
    </div>
  );
}
